import React from "react";
import { Icon, IconButton, MenuItem, Select } from "../../../shared/components";
import { useTooltipContainer } from "../../../shared/providers/TooltipContainerProvider";
import { defaultDirection, SORT_KEY_LABELS, type PassSort, type SortKey } from "../../../store/profiling/sorting";

type Props = {
    sort: PassSort;
    onChange: (sort: PassSort) => void;
};

/**
 * Compact footer of the pass tree: picks the metric the passes (and their nested cycles, by subtree
 * totals) are ordered by, plus a direction toggle.  Picking a key applies its natural direction
 * (metrics hottest-first, start time chronological); the arrow then flips it.
 */
function PassSortBar({ sort, onChange }: Props) {
    // The Select's menu portals — anchor it to the pop-out-aware container (same pattern as the
    // shared Autocomplete's Popper) so it opens in the dev-tools window, not the main one.
    const tooltipContainerRef = useTooltipContainer();

    return (
        <div className="pass-sort-bar">
            <span className="sort-label">Sort by: </span>
            <div className="flex-1"></div>
            <Select
                size="small"
                variant="outlined"
                value={sort.key}
                onChange={e => { const key = e.target.value as SortKey; onChange({ key, direction: defaultDirection(key) }); }}
                MenuProps={{ container: tooltipContainerRef.current }}
                SelectDisplayProps={{ "aria-label": "Sort passes by" }}
            >
                {(Object.keys(SORT_KEY_LABELS) as SortKey[]).map(key => (
                    <MenuItem key={key} value={key} dense>{SORT_KEY_LABELS[key]}</MenuItem>
                ))}
            </Select>
            <IconButton
                size="small"
                aria-label="Toggle sort direction"
                title={sort.direction === "asc" ? "Ascending — click for descending" : "Descending — click for ascending"}
                onClick={() => onChange({ ...sort, direction: sort.direction === "asc" ? "desc" : "asc" })}
            >
                {sort.direction === "asc" ? <Icon>north</Icon> : <Icon>south</Icon>}
            </IconButton>
        </div>
    );
}

export default React.memo(PassSortBar);
